import { Organization } from '../types';

export type OrganizationColorScheme =
  | 'default'
  | 'blue'
  | 'emerald'
  | 'amber'
  | 'rose'
  | 'slate';

export type OrganizationThemeDefault = 'light' | 'dark' | 'system';

export interface OrganizationAppearance {
  colorScheme: OrganizationColorScheme;
  defaultTheme?: OrganizationThemeDefault;
}

interface PaletteValues {
  [variableName: string]: string;
}

interface ColorPalette {
  light: PaletteValues;
  dark: PaletteValues;
}

const COLOR_SCHEME_OPTIONS: Array<{ value: OrganizationColorScheme; label: string }> = [
  { value: 'default', label: 'Default' },
  { value: 'blue', label: 'Blue' },
  { value: 'emerald', label: 'Emerald' },
  { value: 'amber', label: 'Amber' },
  { value: 'rose', label: 'Rose' },
  { value: 'slate', label: 'Slate' },
];

const THEME_DEFAULT_OPTIONS: Array<{ value: OrganizationThemeDefault; label: string }> = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

const ORG_COLOR_PALETTES: Record<Exclude<OrganizationColorScheme, 'default'>, ColorPalette> = {
  blue: {
    light: {
      '--primary': '#2563eb',
      '--primary-foreground': '#ffffff',
      '--accent': '#dbeafe',
      '--accent-foreground': '#1e3a8a',
      '--ring': '#3b82f6',
      '--sidebar-primary': '#2563eb',
      '--sidebar-primary-foreground': '#ffffff',
      '--chart-1': '#2563eb',
      '--chart-2': '#0ea5e9',
      '--chart-3': '#1d4ed8',
      '--chart-4': '#0284c7',
      '--chart-5': '#0891b2',
    },
    dark: {
      '--primary': '#60a5fa',
      '--primary-foreground': '#0f172a',
      '--accent': '#1e3a8a',
      '--accent-foreground': '#dbeafe',
      '--ring': '#93c5fd',
      '--sidebar-primary': '#60a5fa',
      '--sidebar-primary-foreground': '#0f172a',
      '--chart-1': '#60a5fa',
      '--chart-2': '#38bdf8',
      '--chart-3': '#818cf8',
      '--chart-4': '#22d3ee',
      '--chart-5': '#0ea5e9',
    },
  },
  emerald: {
    light: {
      '--primary': '#059669',
      '--primary-foreground': '#ffffff',
      '--accent': '#d1fae5',
      '--accent-foreground': '#065f46',
      '--ring': '#10b981',
      '--sidebar-primary': '#059669',
      '--sidebar-primary-foreground': '#ffffff',
      '--chart-1': '#059669',
      '--chart-2': '#16a34a',
      '--chart-3': '#0f766e',
      '--chart-4': '#65a30d',
      '--chart-5': '#22c55e',
    },
    dark: {
      '--primary': '#34d399',
      '--primary-foreground': '#032b23',
      '--accent': '#065f46',
      '--accent-foreground': '#d1fae5',
      '--ring': '#6ee7b7',
      '--sidebar-primary': '#34d399',
      '--sidebar-primary-foreground': '#032b23',
      '--chart-1': '#34d399',
      '--chart-2': '#4ade80',
      '--chart-3': '#2dd4bf',
      '--chart-4': '#a3e635',
      '--chart-5': '#10b981',
    },
  },
  amber: {
    light: {
      '--primary': '#d97706',
      '--primary-foreground': '#ffffff',
      '--accent': '#fef3c7',
      '--accent-foreground': '#92400e',
      '--ring': '#f59e0b',
      '--sidebar-primary': '#d97706',
      '--sidebar-primary-foreground': '#ffffff',
      '--chart-1': '#d97706',
      '--chart-2': '#f59e0b',
      '--chart-3': '#ea580c',
      '--chart-4': '#ca8a04',
      '--chart-5': '#fbbf24',
    },
    dark: {
      '--primary': '#fbbf24',
      '--primary-foreground': '#3f2204',
      '--accent': '#78350f',
      '--accent-foreground': '#fef3c7',
      '--ring': '#fcd34d',
      '--sidebar-primary': '#fbbf24',
      '--sidebar-primary-foreground': '#3f2204',
      '--chart-1': '#fbbf24',
      '--chart-2': '#fb923c',
      '--chart-3': '#f59e0b',
      '--chart-4': '#facc15',
      '--chart-5': '#f97316',
    },
  },
  rose: {
    light: {
      '--primary': '#e11d48',
      '--primary-foreground': '#ffffff',
      '--accent': '#ffe4e6',
      '--accent-foreground': '#9f1239',
      '--ring': '#f43f5e',
      '--sidebar-primary': '#e11d48',
      '--sidebar-primary-foreground': '#ffffff',
      '--chart-1': '#e11d48',
      '--chart-2': '#f43f5e',
      '--chart-3': '#ec4899',
      '--chart-4': '#f97316',
      '--chart-5': '#ef4444',
    },
    dark: {
      '--primary': '#fb7185',
      '--primary-foreground': '#4a0d1d',
      '--accent': '#9f1239',
      '--accent-foreground': '#ffe4e6',
      '--ring': '#fda4af',
      '--sidebar-primary': '#fb7185',
      '--sidebar-primary-foreground': '#4a0d1d',
      '--chart-1': '#fb7185',
      '--chart-2': '#f472b6',
      '--chart-3': '#f97316',
      '--chart-4': '#f43f5e',
      '--chart-5': '#ec4899',
    },
  },
  slate: {
    light: {
      '--primary': '#475569',
      '--primary-foreground': '#ffffff',
      '--accent': '#e2e8f0',
      '--accent-foreground': '#1e293b',
      '--ring': '#64748b',
      '--sidebar-primary': '#475569',
      '--sidebar-primary-foreground': '#ffffff',
      '--chart-1': '#475569',
      '--chart-2': '#0f766e',
      '--chart-3': '#0284c7',
      '--chart-4': '#334155',
      '--chart-5': '#6366f1',
    },
    dark: {
      '--primary': '#94a3b8',
      '--primary-foreground': '#0f172a',
      '--accent': '#334155',
      '--accent-foreground': '#e2e8f0',
      '--ring': '#cbd5e1',
      '--sidebar-primary': '#94a3b8',
      '--sidebar-primary-foreground': '#0f172a',
      '--chart-1': '#94a3b8',
      '--chart-2': '#2dd4bf',
      '--chart-3': '#38bdf8',
      '--chart-4': '#a78bfa',
      '--chart-5': '#64748b',
    },
  },
};

const ORG_THEME_VARIABLES = [
  '--primary',
  '--primary-foreground',
  '--accent',
  '--accent-foreground',
  '--ring',
  '--sidebar-primary',
  '--sidebar-primary-foreground',
  '--chart-1',
  '--chart-2',
  '--chart-3',
  '--chart-4',
  '--chart-5',
] as const;

const isColorScheme = (value: unknown): value is OrganizationColorScheme =>
  typeof value === 'string' && COLOR_SCHEME_OPTIONS.some((option) => option.value === value);

const isThemeDefault = (value: unknown): value is OrganizationThemeDefault =>
  typeof value === 'string' && THEME_DEFAULT_OPTIONS.some((option) => option.value === value);

const getRecord = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== 'object') {
    return {};
  }
  return value as Record<string, unknown>;
};

const pickValue = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return undefined;
};

export const resolveOrganizationAppearance = (
  organization: Organization | null | undefined
): OrganizationAppearance => {
  const settings = getRecord(organization?.settings);
  const appearance = getRecord(settings.appearance);
  const theme = getRecord(settings.theme);

  const colorSchemeCandidate = pickValue(
    appearance.colorScheme,
    settings.colorScheme,
    settings.orgColorScheme,
    theme.colorScheme
  );

  const defaultThemeCandidate = pickValue(
    appearance.defaultTheme,
    appearance.theme,
    settings.defaultTheme,
    settings.themeDefault,
    theme.defaultTheme,
    theme.mode
  );

  return {
    colorScheme: isColorScheme(colorSchemeCandidate) ? colorSchemeCandidate : 'default',
    defaultTheme: isThemeDefault(defaultThemeCandidate) ? defaultThemeCandidate : undefined,
  };
};

export const applyOrganizationColorScheme = (
  root: HTMLElement,
  colorScheme: OrganizationColorScheme,
  effectiveTheme: 'light' | 'dark'
): void => {
  ORG_THEME_VARIABLES.forEach((variableName) => {
    root.style.removeProperty(variableName);
  });

  if (colorScheme === 'default') {
    return;
  }

  const palette = ORG_COLOR_PALETTES[colorScheme]?.[effectiveTheme];

  if (!palette) {
    return;
  }

  Object.entries(palette).forEach(([variableName, value]) => {
    root.style.setProperty(variableName, value);
  });
};

export const getOrganizationColorSchemeOptions = () => COLOR_SCHEME_OPTIONS;

export const getOrganizationThemeDefaultOptions = () => THEME_DEFAULT_OPTIONS;
